import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Avatar, AvatarProps, styled, Tooltip } from '@mui/material'
import { DisplayableUser } from './model'
import { notProp } from '@portal/pkg-utils'
import { stringToColor } from '@portal/pkg-ui'
import { UserAvatarSize, userAvatarSizeStyles } from './userAvatarStyles'

export interface UserAvatarProps extends DisplayableUser, Omit<AvatarProps, 'alt'> {
  /**
   * Override the displayName, defaults to localized familyName/givenName combination.
   */
  displayNameOverride?: string
  /**
   * Override the monogram, defaults to localized `displayName`.
   */
  monogramOverride?: string
  size?: UserAvatarSize
}

const Root = styled(Avatar, {
  name: 'UserAvatar',
  slot: 'Root',
  shouldForwardProp: notProp(
    'size',
    'color',
    'identifier',
    'displayName',
    'displayNameOverride',
    'monogramOverride',
  ),
})<UserAvatarProps>(({ color, identifier, size = 'medium' }) => ({
  backgroundColor: color || stringToColor(identifier),
  ...userAvatarSizeStyles(size),
}))
export const UserAvatar: React.FC<UserAvatarProps> = (props) => {
  const { displayNameOverride, displayName, familyName, givenName, identifier, monogramOverride } =
    props
  const { t } = useTranslation()

  const name = useMemo(() => {
    if (displayNameOverride) {
      return displayNameOverride
    }
    if (displayName) {
      return displayName
    }
    if (familyName || givenName) {
      return t('displayName', { familyName, givenName })
    }
    return identifier
  }, [displayNameOverride, displayName, familyName, givenName, identifier, t])

  return (
    <Tooltip title={displayName}>
      <Root {...props}>{monogramOverride || t('monogram', { displayName: name })}</Root>
    </Tooltip>
  )
}
